/**
 * BatteryWidget — индикатор Plasma Core в шапке.
 *
 * Показывается только если у юзера есть хотя бы один активный
 * Plasma Core (`plasmaCoreActive=true`). Визуально — иконка батарейки
 * с CSS-заливкой по проценту остаточного заряда.
 *
 * При тапе — модалка со всей статистикой:
 *   - текущий заряд + сколько дней осталось
 *   - количество PC слотов
 *   - сколько заработано через PC за всё время
 *   - что это и зачем (+25% LPS, авто-сбор)
 *
 * Анимация при появлении: батарейка плавно "заполняется" от 0% до
 * текущего значения через CSS transition (надёжнее motion.rect на SVG).
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { useDashboardUser } from '@/hooks/useDashboardUser';
import plasmaCoreAsset from '@/assets/plasma-core-gold.webp';

// Полный заряд PC = 7 дней (TZ).
const FULL_CHARGE_MS = 7 * 24 * 60 * 60 * 1000;

function formatTimeLeft(ms: number, t: TFunction): string {
  if (ms <= 0) return t('battery.discharged');
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  if (days > 0) return t('battery.time.days', { days, hours, mm, ss });
  if (hours > 0) return t('battery.time.hours', { hours, mm, ss });
  return t('battery.time.minutes', { mm, ss });
}

export function BatteryWidget() {
  const data = useDashboardUser();
  const uiSounds = useUISounds();
  const { haptic } = useTelegramWebApp();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Стартуем с 0 и переключаем на реальное значение через 1 тик —
  // CSS transition даёт плавное "наполнение" от 0% до chargePct.
  const [animateFill, setAnimateFill] = useState(false);

  const isVisible = data?.plasmaCoreActive === true;

  // Тик обновления заряда — КАЖДЫЕ 10 секунд вместо каждой секунды.
  // Разница визуально незаметна (заряд меняется <0.01% за 10 сек),
  // но FPS и CPU идут в 10 раз меньше. Раньше каждый 1 сек триггер
  // ре-рендера всего header-widget-а — это было ощутимо на iOS.
  useEffect(() => {
    if (!isVisible) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 10_000);
    return () => clearInterval(id);
  }, [isVisible]);

  // Триггерим заполнение через RAF после первого рендера
  useEffect(() => {
    if (!isVisible) return;
    const id = window.requestAnimationFrame(() =>
      window.setTimeout(() => setAnimateFill(true), 50),
    );
    return () => window.cancelAnimationFrame(id);
  }, [isVisible]);

  if (!data || !isVisible) return null;

  // untilMs — МИНИМУМ (ближайший к истечению PC), для time-left countdown.
  const untilMs = data.plasmaCoreUntil ?? 0;
  const msLeft = Math.max(0, untilMs - nowMs);

  // % заряда — СРЕДНЕЕ по всем активным PC (серверный расчёт AVG).
  // Плавно уменьшается: при истечении одного слота прыжок минимален,
  // в отличие от MAX/MIN, которые резко скакали из-за выбывания крайнего.
  // Между 30-сек poll'ами используем last-known server value — оно
  // сдвигается за счёт CSS transition на fill-баре.
  const chargePct = Math.max(
    0, Math.min(100, data.pcAvgChargePct ?? (msLeft / FULL_CHARGE_MS) * 100),
  );
  void nowMs; // ref'им чтобы useState-апдейт вызывал ре-рендер
  // Цвет заряда: > 50% зелёный · 20–50% жёлтый · < 20% красный
  const chargeColor =
    chargePct > 50
      ? 'var(--status-positive)'
      : chargePct > 20
        ? 'var(--status-warning)'
        : 'var(--status-negative)';

  const fillWidthPx = animateFill ? (15 * chargePct) / 100 : 0;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic.light();
    uiSounds.plasmaCoreTap();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        aria-label={t('battery.buttonAria')}
        onClick={handleOpen}
        data-no-sound="true"
        className="relative h-9 px-2.5 flex items-center gap-1.5 rounded-full lg-pill cursor-pointer active:scale-[0.95] transition-transform"
      >
        {/* Battery SVG: рамка + наконечник + заливка с CSS transition */}
        <svg width="22" height="14" viewBox="0 0 22 14" aria-hidden>
          <rect
            x="0.5"
            y="0.5"
            width="18"
            height="13"
            rx="2.5"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="1"
          />
          <rect
            x="19"
            y="4"
            width="2.5"
            height="6"
            rx="1"
            fill="var(--text-muted)"
          />
          <rect
            x="2"
            y="2"
            width={fillWidthPx}
            height="10"
            rx="1"
            fill={chargeColor}
            style={{
              transition: 'width 1s cubic-bezier(0.32, 0.72, 0, 1)',
            }}
          />
        </svg>
        <span
          className="text-[10px] font-mono font-bold tabular-nums"
          style={{ color: chargeColor }}
        >
          {Math.round(chargePct)}%
        </span>
      </button>

      {/* Портал — выносим модалку из AppHeader (sticky + backdrop-filter
          создают stacking context, который клипит fixed-позиционирование
          и ломает центровку — модалка улетает вверх). */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <BatteryModal
              chargePct={chargePct}
              chargeColor={chargeColor}
              msLeft={msLeft}
              pcContractsCount={data.pcContractsCount}
              pcLifetimeEarned={data.pcLifetimeEarned ?? 0}
              onClose={() => setOpen(false)}
            />
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

// ── Модалка со всей статистикой ───────────────────────────────────
function BatteryModal({
  chargePct,
  chargeColor,
  msLeft,
  pcContractsCount,
  pcLifetimeEarned,
  onClose,
}: {
  chargePct: number;
  chargeColor: string;
  msLeft: number;
  pcContractsCount: number;
  pcLifetimeEarned: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const uiSounds = useUISounds();
  const onClickStop = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
  const handleBackdrop = useCallback(() => { uiSounds.bellClose(); onClose(); }, [uiSounds, onClose]);
  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center px-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={handleBackdrop}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        className="relative w-full max-w-sm rounded-3xl p-6"
        style={{
          background:
            'linear-gradient(180deg, rgba(28,28,42,0.98) 0%, rgba(18,18,30,0.98) 100%)',
          border: '1px solid var(--glass-stroke-strong)',
          boxShadow:
            '0 30px 80px -20px rgba(0,0,0,0.7), inset 0 1px 0 0 var(--glass-rim-light)',
        }}
        initial={{ y: 30, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 20, opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        onClick={onClickStop}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          aria-label={t('battery.closeAria')}
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <img
            src={plasmaCoreAsset}
            alt="Plasma Core"
            className="h-14 w-14 flex-shrink-0 object-contain"
            style={{
              filter:
                'drop-shadow(0 0 14px rgba(255,160,50,0.45)) drop-shadow(0 4px 10px rgba(0,0,0,0.4))',
            }}
          />
          <div>
            <h3 className="text-[16px] font-extrabold text-[var(--text-primary)]">
              {t('battery.title')}
            </h3>
            <p className="text-[11px] text-[var(--text-muted)]">
              {t('battery.subtitle')}
            </p>
          </div>
        </div>

        {/* Блок текущего заряда */}
        <div
          className="rounded-2xl p-4 mb-3"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--glass-stroke)',
          }}
        >
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
              {t('battery.charge')}
            </span>
            <span
              className="text-[16px] font-extrabold tabular-nums"
              style={{ color: chargeColor }}
            >
              {Math.round(chargePct)}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${chargePct}%`,
                background: `linear-gradient(90deg, ${chargeColor}, color-mix(in oklab, ${chargeColor} 50%, white))`,
                boxShadow: `0 0 8px ${chargeColor}`,
                transition: 'width 0.8s cubic-bezier(0.32,0.72,0,1)',
              }}
            />
          </div>
          <div className="flex items-baseline justify-between mt-3">
            <span className="text-[11px] text-[var(--text-muted)]">
              {t('battery.left')}
            </span>
            <span className="text-[12px] font-bold text-[var(--text-secondary)] tabular-nums">
              {formatTimeLeft(msLeft, t)}
            </span>
          </div>
        </div>

        {/* Блок статистики */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div
            className="rounded-2xl p-3"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--glass-stroke)',
            }}
          >
            <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
              {t('battery.slotsLabel')}
            </p>
            <p className="text-[18px] font-extrabold text-[var(--text-primary)] mt-1 tabular-nums">
              {pcContractsCount}
            </p>
          </div>
          <div
            className="rounded-2xl p-3"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--glass-stroke)',
            }}
          >
            <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
              {t('battery.earnedLabel')}
            </p>
            <p className="text-[18px] font-extrabold text-[var(--status-positive)] mt-1 tabular-nums">
              {pcLifetimeEarned.toFixed(2)}
              <span className="text-[10px] font-medium text-[var(--text-muted)] ml-1">
                USD₮
              </span>
            </p>
          </div>
        </div>

        {/* Кнопка "Перезарядить все" */}
        <RechargeAllButton
          count={pcContractsCount}
          chargePct={chargePct}
          onFinish={onClose}
        />

        {/* Что это даёт */}
        <div className="space-y-2 mt-4 mb-1">
          <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)] mb-2">
            {t('battery.benefits.title')}
          </p>
          <BulletRow text={t('battery.benefits.b1')} />
          <BulletRow text={t('battery.benefits.b2')} />
          <BulletRow text={t('battery.benefits.b3')} />
          <BulletRow text={t('battery.benefits.b4')} />
        </div>
      </motion.div>
    </motion.div>
  );
}

// 10 шагов анимации по ключам — фактический текст локализуется.
const RECHARGE_STEP_KEYS = [
  'battery.steps.s1',
  'battery.steps.s2',
  'battery.steps.s3',
  'battery.steps.s4',
  'battery.steps.s5',
  'battery.steps.s6',
  'battery.steps.s7',
  'battery.steps.s8',
  'battery.steps.s9',
  'battery.steps.s10',
] as const;

interface RechargeResultData {
  count: number;
  cost: string;
  until: string;
}

/**
 * Кнопка "Перезарядить все" + WOW-анимация 5с + отчёт,
 * который остаётся до тапа юзера (для скринов соц-сетей).
 */
function RechargeAllButton({
  count,
  chargePct,
  onFinish,
}: {
  count: number;
  chargePct: number;
  onFinish: () => void;
}) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [state, setState] = useState<'idle' | 'animating' | 'success' | 'error'>('idle');
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState<RechargeResultData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const cost = count;

  // Защита от дурака: повторные перезарядки при почти полном заряде —
  // классическая жалоба "почему я заплатил несколько раз". Блокируем
  // кнопку пока заряд ≥ 80%; кнопка серая, hint поясняет правило.
  const blockedByCharge = chargePct >= 80 && state === 'idle';

  const handleClick = async () => {
    if (state === 'animating') return;
    if (blockedByCharge) return;
    haptic.medium();
    uiSounds.plasmaCoreActivate();
    setState('animating');
    setStepIdx(0);
    setResult(null);
    setErrorMsg('');

    // Таймеры статусов — каждые 500мс
    const timers: number[] = [];
    RECHARGE_STEP_KEYS.forEach((_, i) => {
      timers.push(
        window.setTimeout(() => {
          setStepIdx(i);
          if (i === RECHARGE_STEP_KEYS.length - 1) haptic.success();
          else if (i % 2 === 0) haptic.light();
        }, i * 500),
      );
    });

    try {
      const { lunaApi } = await import('@/services/api');
      // Параллельно: API + мин 5 сек анимации
      const apiPromise = lunaApi.plasmaCore.rechargeAll();
      const minDelay = new Promise((r) => setTimeout(r, 5000));
      const [res] = await Promise.all([apiPromise, minDelay]);
      const data = res.data as {
        success: boolean;
        rechargedCount?: number;
        costPaid?: string;
        newCycleUntil?: string;
      };
      if (data.success) {
        setResult({
          count: data.rechargedCount ?? count,
          cost: String(parseFloat(data.costPaid ?? String(cost))),
          until: data.newCycleUntil ?? '',
        });
        setState('success');
        haptic.success();
        uiSounds.plasmaCoreRecharge();
      }
    } catch (err) {
      await new Promise((r) => setTimeout(r, 500));
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('battery.recharge.error');
      setErrorMsg(msg);
      setState('error');
      haptic.error();
      uiSounds.toastError();
    } finally {
      timers.forEach(clearTimeout);
    }
  };

  if (count === 0) return null;

  return (
    <>
      <div className="mt-4">
        <button
          type="button"
          onClick={handleClick}
          disabled={state === 'animating' || state === 'success' || blockedByCharge}
          data-no-sound="true"
          className="w-full rounded-2xl py-3.5 text-[14px] font-extrabold transition active:scale-[0.97] disabled:cursor-not-allowed"
          style={
            blockedByCharge
              ? {
                  background:
                    'linear-gradient(180deg, var(--glass-fill-strong), var(--glass-fill))',
                  border: '1px solid var(--glass-stroke-strong)',
                  color: 'var(--text-disabled)',
                  boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.06)',
                  opacity: 0.7,
                }
              : {
                  background: 'linear-gradient(180deg, #FFC85A, #FF8C32)',
                  border: '1px solid rgba(255,200,80,0.5)',
                  color: '#0a0a0f',
                  boxShadow: '0 6px 20px -6px rgba(255,160,50,0.55)',
                  opacity: state === 'animating' ? 0.6 : 1,
                }
          }
        >
          {state === 'success'
            ? t('battery.recharge.done')
            : blockedByCharge
              ? t('battery.recharge.ctaDisabled')
              : t('battery.recharge.cta', { count, cost })}
        </button>
        {blockedByCharge && (
          <p className="text-[11px] mt-2 text-center text-[var(--text-muted)]">
            {t('battery.recharge.disabledHint')}
          </p>
        )}
        {state === 'error' && errorMsg && (
          <p className="text-[11px] font-mono mt-2 text-center text-[var(--status-negative)]">
            {errorMsg}
          </p>
        )}
      </div>

      {/* WOW-анимация + отчёт: оверлей внутри модалки.
          "Закрыть" в оверлее возвращает к кнопке — можно перезарядить
          ещё раз в рамках того же открытия модалки. */}
      <AnimatePresence>
        {(state === 'animating' || state === 'success') && (
          <RechargeOverlay
            step={stepIdx}
            result={result}
            onClose={() => {
              setState('idle');
              // Если уже успех — закрываем всю модалку PC,
              // возвращаемся на главную.
              if (result) onFinish();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/** Оверлей поверх BatteryModal — анимация + финальный отчёт */
function RechargeOverlay({
  step,
  result,
  onClose,
}: {
  step: number;
  result: RechargeResultData | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const isDone = result !== null;

  const handleClose = useCallback(() => {
    haptic.medium();
    onClose();
  }, [haptic, onClose]);
  const untilPretty = result?.until
    ? new Date(result.until).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <motion.div
      className="absolute inset-0 rounded-3xl flex flex-col items-center justify-center p-6 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(10,8,0,0.98) 0%, rgba(18,12,0,0.99) 100%)',
        backdropFilter: 'blur(20px)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Расходящиеся кольца — плавно гаснут за 5с после завершения */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ opacity: isDone ? 0 : 1 }}
        transition={{ duration: isDone ? 5 : 0, ease: 'easeOut' }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={`ring-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: '50%', top: '50%',
              width: 40, height: 40, marginLeft: -20, marginTop: -20,
              border: '1.5px solid #FFB347',
            }}
            animate={{ scale: [1, 6], opacity: [0.6, 0] }}
            transition={{
              duration: 2.5, delay: i * 0.5,
              ease: [0.22, 1, 0.36, 1],
              repeat: isDone ? 0 : Infinity,
              repeatDelay: 0.2,
            }}
          />
        ))}
      </motion.div>

      {/* Частицы-искры */}
      {!isDone && [...Array(10)].map((_, i) => {
        const angle = (i / 10) * Math.PI * 2;
        return (
          <motion.div
            key={`p-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 3, height: 3, left: '50%', top: '50%',
              background: '#FFC85A',
              boxShadow: '0 0 10px #FFB347',
            }}
            animate={{
              x: [Math.cos(angle) * 130, 0],
              y: [Math.sin(angle) * 130, 0],
              opacity: [0, 1, 1, 0],
              scale: [0.3, 1.4, 1, 0],
            }}
            transition={{
              duration: 3, delay: 0.5 + i * 0.15,
              ease: [0.32, 0.72, 0, 1],
              repeat: Infinity,
            }}
          />
        );
      })}

      {/* Пульсирующая иконка */}
      <motion.img
        src={plasmaCoreAsset}
        alt="Plasma Core"
        className="relative z-10 mb-5"
        style={{
          width: 110, height: 110,
          filter: 'drop-shadow(0 0 28px rgba(255,160,50,0.75))',
          top: 15,
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: isDone ? 1.1 : [0, 1.1, 1, 1.15, 1.05, 1.2, 1],
          opacity: 1,
        }}
        transition={{ duration: 4.5, ease: 'easeInOut' }}
      />

      {!isDone && (
        // Статусы во время анимации
        <div className="relative z-10 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={step}
              className="text-[11px] font-mono font-extrabold uppercase tracking-[0.18em]"
              style={{ color: step === RECHARGE_STEP_KEYS.length - 1 ? 'var(--status-positive)' : '#FFC85A' }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
            >
              {t(RECHARGE_STEP_KEYS[step] ?? RECHARGE_STEP_KEYS[0])}
            </motion.p>
          </AnimatePresence>
        </div>
      )}

      {isDone && result && (
        // Финальный отчёт — остаётся до закрытия
        <motion.div
          className="relative z-10 text-center w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        >
          <p
            className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
            style={{ color: 'var(--status-positive)' }}
          >
            {t('battery.recharge.headerDone')}
          </p>
          <h3
            className="text-[26px] font-display font-extrabold mb-5"
            style={{ color: '#FFC85A' }}
          >
            {t('battery.recharge.titleN', { count: result.count })}
          </h3>

          <div
            className="rounded-2xl p-3 space-y-2 text-left"
            style={{
              background: 'rgba(255,200,80,0.06)',
              border: '1px solid rgba(255,200,80,0.25)',
            }}
          >
            <Row label={t('battery.recharge.rowExtended')} value={`${result.count}`} />
            <Row label={t('battery.recharge.rowCharged')} value={`${result.cost} USD₮`} />
            <Row label={t('battery.recharge.rowNewCycle')} value={t('battery.recharge.rowCycleValue')} />
            <Row label={t('battery.recharge.rowUntil')} value={untilPretty} />
          </div>

          <motion.button
            type="button"
            onClick={handleClose}
            onTapStart={() => haptic.light()}
            className="relative mt-5 w-full rounded-2xl py-3.5 text-[13px] font-extrabold overflow-hidden select-none"
            style={{
              background: 'linear-gradient(180deg, #FFC85A 0%, #FF8C32 100%)',
              color: '#0a0a0f',
              boxShadow:
                '0 10px 26px -8px rgba(255,160,50,0.65), 0 2px 0 rgba(140,60,0,0.35), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(120,50,0,0.25)',
              WebkitTapHighlightColor: 'transparent',
              transform: 'translateZ(0)',
            }}
            whileTap={{
              scale: 0.92,
              y: 2,
              filter: 'brightness(0.88)',
              boxShadow:
                '0 2px 6px -3px rgba(255,160,50,0.7), inset 0 3px 10px rgba(120,50,0,0.55), inset 0 -1px 0 rgba(255,255,255,0.1)',
            }}
            transition={{ type: 'spring', stiffness: 520, damping: 22, mass: 0.6 }}
          >
            {/* Вспышка-ripple из центра при нажатии */}
            <motion.span
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at center, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 65%)',
              }}
              initial={{ opacity: 0, scale: 0.2 }}
              whileTap={{ opacity: [0, 1, 0], scale: [0.2, 1.6, 1.9] }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            />
            {/* Верхний глянец-блик */}
            <span
              className="absolute inset-x-0 top-0 h-1/2 pointer-events-none rounded-t-2xl"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)',
              }}
            />
            <span className="relative z-10 tracking-wide">{t('battery.close')}</span>
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono">
        {label}
      </span>
      <span className="text-[13px] font-extrabold text-[var(--text-primary)] tabular-nums">
        {value}
      </span>
    </div>
  );
}

function BulletRow({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2">
      <span
        className="mt-1.5 h-1 w-1 rounded-full flex-shrink-0"
        style={{ background: '#FFC85A' }}
      />
      <p className="text-[12px] text-[var(--text-secondary)] leading-[1.5]">
        {text}
      </p>
    </div>
  );
}
